// Sync tab — Stax Bill connection, run controls, live progress and run history.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, Database, CalendarDays, AlertTriangle, CheckCircle2, XCircle, Play } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money, fmtWhen, apiError, kindLabel, thCls, tdCls } from "./engineShared";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{value}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  if (status === "running") return <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100"><Loader2 className="mr-1 h-3 w-3 animate-spin" />running</Badge>;
  if (status === "done") return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="mr-1 h-3 w-3" />done</Badge>;
  return <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100"><XCircle className="mr-1 h-3 w-3" />{status}</Badge>;
};

export function EngineSyncPanel({ overview, reload, canManage, superAdmin }) {
  const [runs, setRuns] = useState([]);
  const [month, setMonth] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const running = overview?.running;

  const loadRuns = async () => {
    try { const { data } = await api.get("/commissions/engine/sync/runs", { params: { limit: 15 } }); setRuns(data.items || []); } catch { /* ignore */ }
  };
  useEffect(() => { loadRuns(); }, [overview?.running?.status, overview?.last_run?.run_id]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => { reload(); }, 4000);
    return () => clearInterval(t);
  }, [running?.run_id, reload]);
  useEffect(() => {
    if (!detail?.run_id) return undefined;
    const tick = async () => { try { const { data } = await api.get(`/commissions/engine/sync/runs/${detail.run_id}`); setDetail(data); } catch { /* ignore */ } };
    tick();
    if (detail.status !== "running") return undefined;
    const t = setInterval(tick, 4000);
    return () => clearInterval(t);
  }, [detail?.run_id, detail?.status]);

  const start = async (months) => {
    setBusy(true);
    try {
      const { data } = await api.post("/commissions/engine/sync", { months, force: months ? force : false });
      toast.success(months ? `Resync of ${months.join(", ")} started` : "Daily sync started");
      setDetail(data.run);
      reload();
    } catch (e) { apiError(e, "Could not start the sync"); } finally { setBusy(false); }
  };
  const toggleDaily = async (v) => {
    try { await api.put("/commissions/engine/settings", { daily_sync_enabled: v }); toast.success(v ? "Daily sync on" : "Daily sync paused"); reload(); }
    catch (e) { apiError(e); }
  };
  const legacyImport = async () => {
    if (!window.confirm("Import the legacy commissions database? Existing legacy statements are replaced and every month is rolled up again.")) return;
    setBusy(true);
    try { const { data } = await api.post("/commissions/engine/legacy-import"); toast.success("Legacy import started"); setDetail(data.run); reload(); }
    catch (e) { apiError(e, "Could not start the import"); } finally { setBusy(false); }
  };

  const p = running?.progress || {};
  return (
    <div className="space-y-4" data-testid="engine-sync-panel">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
        <Stat label="Statement lines" value={(overview?.statements || 0).toLocaleString()} testid="engine-stat-statements" />
        <Stat label="Months" value={overview?.months?.length || 0} testid="engine-stat-months" />
        <Stat label="Active agents" value={`${overview?.active_agents || 0} / ${overview?.agents || 0}`} testid="engine-stat-agents" />
        <Stat label="Deals" value={overview?.deal_terms || 0} testid="engine-stat-deals" />
        <Stat label="Rate cells" value={overview?.rates || 0} testid="engine-stat-rates" />
        <Stat label="Lines with no rate" value={(overview?.no_rate_lines || 0).toLocaleString()} testid="engine-stat-norate" />
      </div>

      <Card className="border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Database className="h-4 w-4 text-slate-500" /> Stax Bill
              {overview?.stax_connected
                ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="engine-stax-connected">connected</Badge>
                : <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100" data-testid="engine-stax-disconnected">not connected</Badge>}
            </div>
            <p className="mt-1 text-xs text-slate-500 max-w-xl">
              Every day at 04:00 MDT the engine recomputes the current and previous month for agent territories, refreshes paid status three months back, and rebuilds the month rollups. Paid invoices are frozen; use a forced resync to recompute them.
            </p>
            <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
              <Switch checked={!!overview?.daily_sync_enabled} onCheckedChange={toggleDaily} disabled={!canManage} data-testid="engine-daily-switch" />
              Daily sync {overview?.daily_sync_enabled ? "on" : "paused"}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button onClick={() => start(undefined)} disabled={!canManage || busy || !!running} data-testid="engine-sync-now">
              {busy || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync now
            </Button>
            <div className="flex items-center gap-2">
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="engine-resync-month" />
              {superAdmin && (
                <label className="flex items-center gap-1 text-xs text-slate-600">
                  <Switch checked={force} onCheckedChange={setForce} data-testid="engine-resync-force" /> force
                </label>
              )}
              <Button variant="outline" className="h-9" onClick={() => start([month])} disabled={!canManage || !month || busy || !!running} data-testid="engine-resync-btn">
                <CalendarDays className="h-4 w-4" /> Resync month
              </Button>
            </div>
          </div>
        </div>
        {running && (
          <div className="mt-4 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900" data-testid="engine-running-box">
            <div className="flex items-center gap-2 font-medium"><Loader2 className="h-4 w-4 animate-spin" />
              {running.kind === "legacy_import" ? `Legacy import — ${running.stage || "…"}`
                : running.kind === "bundle_export" || running.kind === "bundle_import" ? `${kindLabel(running)} — ${running.stage || "…"}`
                  : `Syncing ${p.month || running.months?.join(", ")}`}
            </div>
            {running.kind === "legacy_import"
              ? <div className="mt-1 text-xs">{(running.statements_done || 0).toLocaleString()} statements imported</div>
              : running.kind === "bundle_export" || running.kind === "bundle_import"
                ? <div className="mt-1 text-xs">{(p.done || 0).toLocaleString()} / {(p.total || 0).toLocaleString()} documents</div>
                : <div className="mt-1 text-xs">{p.invoices_done || 0} / {p.invoices_total || 0} invoices · {p.fetched || 0} re-read · {p.lines || 0} lines · {p.paid_updates || 0} paid updates · {p.voided || 0} voided lines</div>}
          </div>
        )}
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-900">Run history</div>
          <Button variant="ghost" size="sm" onClick={loadRuns} data-testid="engine-runs-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="engine-runs-table">
            <thead className="bg-slate-50"><tr>
              <th className={thCls}>Started</th><th className={thCls}>Kind</th><th className={thCls}>Months</th>
              <th className={thCls}>Status</th><th className={thCls}>Result</th><th className={thCls}>By</th><th className={thCls}></th>
            </tr></thead>
            <tbody>
              {runs.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No runs yet</td></tr>}
              {runs.map((r) => (
                <tr key={r.run_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`engine-run-${r.run_id}`}>
                  <td className={`${tdCls} whitespace-nowrap`}>{fmtWhen(r.started_at)}</td>
                  <td className={tdCls}>{kindLabel(r)}{r.force ? " · forced" : ""}</td>
                  <td className={tdCls}>{(r.months || []).join(", ") || "—"}</td>
                  <td className={tdCls}><StatusBadge status={r.status} /></td>
                  <td className={`${tdCls} text-xs text-slate-600`}>
                    {r.kind === "legacy_import" && r.summary
                      ? `${(r.summary.statements || 0).toLocaleString()} lines · ${r.summary.months?.length || 0} months · ${money(r.summary.total_commission)}`
                      : r.kind === "bundle_export" && r.summary
                        ? `${(r.summary.documents || 0).toLocaleString()} documents · ${r.file || "file deleted"}`
                        : r.kind === "bundle_import" && r.summary
                          ? `${Object.values(r.summary.written || {}).reduce((a, b) => a + b, 0).toLocaleString()} documents · ${r.summary.rebuilt} months rolled up · ${money(r.summary.total_commission)}`
                          : r.progress && r.kind === "stax_sync"
                            ? `${r.progress.invoices_done || 0} invoices · ${r.progress.lines || 0} lines · ${r.progress.paid_updates || 0} paid updates${r.unknown_territories?.length ? ` · ${r.unknown_territories.length} unknown territories` : ""}`
                            : "—"}
                    {r.error && <div className="text-rose-600 flex items-center gap-1 mt-0.5"><AlertTriangle className="h-3 w-3" />{r.error}</div>}
                  </td>
                  <td className={`${tdCls} text-slate-500`}>{r.started_by_name || "Scheduler"}</td>
                  <td className={tdCls}><Button variant="ghost" size="sm" onClick={() => setDetail({ run_id: r.run_id, status: r.status })} data-testid={`engine-run-open-${r.run_id}`}>Log</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!(detail && detail.run_id)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="engine-run-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              Run {detail?.run_id} {detail?.status && <StatusBadge status={detail.status} />}
            </DialogTitle>
          </DialogHeader>
          {detail?.unknown_territories?.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              <b>Agent territories with invoices but no agent record</b> (add them on the Agents tab and resync):{" "}
              {detail.unknown_territories.map((u) => `${u.territory} (${u.invoices})`).join(", ")}
            </div>
          )}
          <pre className="max-h-[60vh] overflow-auto rounded-md bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100" data-testid="engine-run-log">
            {(detail?.log || []).join("\n") || (detail?.stage ? `${detail.stage}` : "no log lines")}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
